/**
 * ThreadRow + ConvoHeader — TeamTalk sidebar row + convo top bar.
 * V1.9.30 — WhatsApp-style inbox rows (round color avatar, cleaner layout).
 * V1.9.33 — Group image upload with cropper.
 */
import React from "react";
import {
    UsersThree,
    ArrowLeft,
    DotsThreeVertical,
    Camera,
} from "@phosphor-icons/react";
import { formatWhen, roleLabel } from "./helpers";
import ChatAvatar from "@/components/chat/ChatAvatar";
import ImageCropperSheet from "@/components/chat/ImageCropperSheet";
import api from "@/lib/api";
import { toast } from "sonner";

export function ThreadRow({ t, selected, onClick }) {
    const isGroup = Boolean(t.is_group);
    const displayName = isGroup ? t.name : (t.counterparty?.name || "Teammate");
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`teamtalk-thread-${t.id}`}
            data-role="thread-row"
            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-black/5 transition-colors ${selected ? "bg-black/5" : ""}`}
        >
            <div className="thread-avatar shrink-0">
                <ChatAvatar
                    name={displayName}
                    src={isGroup ? t.image_url : t.counterparty?.avatar_url}
                    isGroup={isGroup}
                    size="md"
                    testId={`teamtalk-thread-avatar-${t.id}`}
                />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                    <p className={`thread-name truncate ${t.unread ? "font-black" : "font-bold"} text-[15px] text-ink`}>
                        {displayName}
                    </p>
                    <span className={`text-[11px] whitespace-nowrap shrink-0 ${t.unread ? "text-emerald-600 font-bold" : "text-ink/45"}`}>
                        {formatWhen(t.last_message_at)}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className={`thread-preview text-[13px] truncate ${t.unread ? "text-ink/80" : "text-ink/55"}`}>
                        {t.last_message_from_me ? "You: " : ""}{t.last_message_preview || " "}
                    </p>
                    {t.unread && (
                        <span
                            data-testid={`teamtalk-unread-dot-${t.id}`}
                            className="min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-black inline-flex items-center justify-center shrink-0"
                        >
                            •
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

export function ConvoHeader({ convo, onBack, onManage, onImageChanged, rightSlot }) {
    const t = convo.thread;
    const isGroup = Boolean(t.is_group);
    const name = isGroup ? t.name : (convo.counterparty?.name || "Teammate");
    const groupImg = isGroup ? t.image_url : undefined;
    const dmImg = !isGroup ? convo.counterparty?.avatar_url : undefined;
    const [pending, setPending] = React.useState(null);  // File chosen but not yet cropped
    const fileRef = React.useRef(null);

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (f) setPending(f);
        e.target.value = "";
    };
    const onCropped = async (blob) => {
        setPending(null);
        try {
            const fd = new FormData();
            fd.append("file", blob, "group.png");
            const r = await api.post(`/team-chat/groups/${t.id}/image`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Group photo updated.");
            onImageChanged?.(r.data.image_url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update photo.");
        }
    };

    return (
        <div className="px-3 py-2.5 border-b border-ink/10 flex items-center gap-3 bg-[#f0f2f5]">
            <button type="button" onClick={onBack}
                    className="md:hidden text-ink/60 hover:text-ink" aria-label="Back"
                    data-testid="teamtalk-back-btn">
                <ArrowLeft size={18} weight="bold" />
            </button>
            <div className="relative shrink-0">
                <ChatAvatar
                    name={name}
                    src={groupImg || dmImg}
                    isGroup={isGroup}
                    size="sm"
                    testId="teamtalk-convo-avatar"
                />
                {isGroup && (
                    <>
                        <input
                            ref={fileRef} type="file" accept="image/*" hidden
                            onChange={onFile}
                            data-testid="teamtalk-group-image-input"
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-[#f0f2f5] hover:bg-emerald-600"
                            aria-label="Change group photo"
                            data-testid="teamtalk-group-image-btn"
                        >
                            <Camera size={11} weight="bold" />
                        </button>
                    </>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-bold text-[15px] truncate text-ink" data-testid="teamtalk-convo-name">
                    {name}
                </p>
                {isGroup ? (
                    <p className="text-[11px] text-ink/50 truncate">
                        {(convo.members || []).map((m) => m.name).slice(0, 4).join(", ")}
                        {(convo.members || []).length > 4 ? ` +${(convo.members || []).length - 4}` : ""}
                    </p>
                ) : (
                    <p className="text-[11px] text-ink/50 truncate">
                        {roleLabel(convo.counterparty?.role)}
                    </p>
                )}
                {t.context_label && (
                    <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5 rounded-sm">
                        {t.context_type}: {t.context_label}
                    </span>
                )}
            </div>
            {isGroup && (
                <button
                    type="button" onClick={onManage}
                    className="text-ink/60 hover:text-ink p-2"
                    aria-label="Manage group"
                    data-testid="teamtalk-manage-group-btn"
                >
                    <DotsThreeVertical size={20} weight="bold" />
                </button>
            )}
            {rightSlot}
            <ImageCropperSheet
                file={pending}
                onCancel={() => setPending(null)}
                onCropped={onCropped}
                title="Set group photo"
            />
        </div>
    );
}
